import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Field, inputCls, btnDark, fmtWhen } from "./atoms";

const FIELDS = [
  { key: "meta_pixel_id", label: "Meta Pixel ID (Facebook / Instagram)", placeholder: "1234567890123456", hint: "Events Manager → Orígenes de datos → ID del píxel." },
  { key: "google_tag_id", label: "Google tag (GA4 y/o Google Ads)", placeholder: "G-XXXXXXXXXX, AW-XXXXXXXXX", hint: "Puedes poner varios separados por coma: la propiedad GA4 (G-…) y la cuenta de Google Ads (AW-…)." },
  { key: "google_ads_conversion_label", label: "Etiqueta de conversión de Google Ads (compra)", placeholder: "AbC-D_efG-h12_34-567", hint: "Opcional. Google Ads → Conversiones → tu acción «Compra» → la parte tras la barra en send_to (AW-xxx/ETIQUETA)." },
  { key: "tiktok_pixel_id", label: "TikTok Pixel ID", placeholder: "C0XXXXXXXXXXXXXXXX", hint: "TikTok Ads Manager → Eventos → Píxel web." },
];

const EVENTS = [
  ["Ver landing del viaje", "ViewContent", "view_item", "ViewContent"],
  ["Abrir «Reserve»", "InitiateCheckout", "begin_checkout", "InitiateCheckout"],
  ["Plazas retenidas (datos del comprador)", "AddPaymentInfo", "add_payment_info", "AddPaymentInfo"],
  ["Pago capturado en PayPal", "Purchase", "purchase (+ conversion)", "CompletePayment"],
];

export function PixelsCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/marketing-pixels").then(({ data }) => setForm(data)).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/marketing-pixels", form);
      setForm((f) => ({ ...f, ...data, updated_at: new Date().toISOString() }));
      toast.success("Píxeles guardados — activos en las landings de Curated Trips");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar");
    } finally { setSaving(false); }
  };

  if (!form) return <Card kicker="Publicidad" title="Píxeles de seguimiento"><Loader2 size={16} className="animate-spin"/></Card>;
  const active = FIELDS.filter((f) => f.key !== "google_ads_conversion_label" && form[f.key]).length;

  return (
    <Card kicker="Publicidad" title="Píxeles de seguimiento y remarketing" testid="pixels-card">
      <p className="text-sm text-clay-700 -mt-2">
        Se cargan <strong>solo en las páginas públicas de Curated Trips</strong> (<code>/curated/…</code> y <code>/pay/curated/…</code>),
        nunca en la herramienta interna ni en los itinerarios a medida. Con ellos puedes medir conversiones y crear audiencias de remarketing
        (quien vio el viaje y no reservó, quien empezó la reserva, quien compró).
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>
            <input
              data-testid={`pixel-${f.key}`}
              className={inputCls}
              placeholder={f.placeholder}
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
          </Field>
        ))}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-clay-600" data-testid="pixels-status">
          {active > 0 ? <span className="text-emerald-700 font-medium">{active} plataforma(s) activa(s)</span> : "Ningún píxel configurado"}
          {form.updated_at && <> · guardado {fmtWhen(form.updated_at)} por {form.updated_by}</>}
        </div>
        <button onClick={save} disabled={saving} className={btnDark} data-testid="pixels-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Guardar píxeles
        </button>
      </div>
      <div className="border-t border-clay-200 pt-4">
        <div className="smallcaps mb-2">Eventos que se envían</div>
        <table className="w-full text-[12px]">
          <thead className="text-clay-500 text-left">
            <tr><th className="py-1 font-medium">Momento</th><th className="py-1 font-medium">Meta</th><th className="py-1 font-medium">Google</th><th className="py-1 font-medium">TikTok</th></tr>
          </thead>
          <tbody>
            {EVENTS.map(([m, fb, g, tt]) => (
              <tr key={m} className="border-t border-clay-100">
                <td className="py-1.5 text-clay-800">{m}</td>
                <td className="py-1.5 font-mono text-clay-700">{fb}</td>
                <td className="py-1.5 font-mono text-clay-700">{g}</td>
                <td className="py-1.5 font-mono text-clay-700">{tt}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-clay-500 mt-2">
          Todos los eventos llevan nombre del viaje, importe y moneda. La compra incluye <code>transaction_id</code> y <code>event_id</code> para
          que las plataformas no la cuenten dos veces. Además, cada reserva guarda los <code>utm_*</code> del anuncio con el que llegó el comprador
          (visible en la pestaña Reservas).
        </p>
      </div>
    </Card>
  );
}
